<script lang="ts" setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  toRefs,
} from 'vue';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import { Site } from '@/types';

const props = withDefaults(
  defineProps<{
    modelValue: Site[];
    keyword?: string;
    items: Site[];
    maxItems?: number;
    hasNextExcludeSites?: boolean;
  }>(),
  {
    maxItems: 10,
    hasNextExcludeSites: false,
  },
);

const emit = defineEmits<{
  'update:modelValue': [selectedItems: Site[]];
  'update:keyword': [keyword: string];
}>();

const { items, modelValue: selectedItems } = toRefs(props);
const currentSelectedItems = reactive({
  items: selectedItems?.value ?? [],
});
const itemListVisible = ref(false);
const hideList = () => (itemListVisible.value = false);

onMounted(() => {
  window.addEventListener('click', hideList);
});
onBeforeUnmount(() => window.removeEventListener('click', hideList));

const addItem = (item: Site) => {
  currentSelectedItems.items?.push(item);
  hideList();
  emit('update:modelValue', currentSelectedItems.items);
};

const removeItem = (item: Site) => {
  currentSelectedItems.items = currentSelectedItems.items.filter(
    i => i.id !== item.id,
  );
  emit('update:modelValue', currentSelectedItems.items);
};

const visibleItems = computed(() => {
  const selectedItemIds = (currentSelectedItems.items ?? []).map(i => i.id);
  return items.value.filter(i => !selectedItemIds.includes(i.id));
});
</script>

<template>
  <div class="item-select">
    <input
      class="c-textInput"
      type="text"
      :value="keyword"
      placeholder="メディア名を入力"
      :disabled="currentSelectedItems.items.length === maxItems"
      @click.stop.prevent="() => undefined"
      @focus="itemListVisible = true"
      @input="
        $emit('update:keyword', ($event.target as HTMLInputElement).value)
      "
    />
    <div class="item-list" v-if="itemListVisible">
      <div class="item disabled heading">
        <div class="c-text-regular--xs">
          <span class="media-label">メディア</span>
          <span>{{ visibleItems.length }}件</span>
        </div>
        <div class="c-text-regular--xs">週間記事数</div>
      </div>
      <div
        class="item"
        v-for="item in visibleItems"
        :key="item.id"
        @click="addItem(item)"
      >
        <div class="info">
          <div class="name c-text-regular--m">{{ item.name }}</div>
          <div class="hostname c-text-regular--xs">{{ item.hostname }}</div>
        </div>
        <div class="count c-text-regular--xs">
          {{ Number(item.article_count_ja).toLocaleString() }}件
        </div>
      </div>
    </div>
    <div class="selected-items" v-if="currentSelectedItems.items.length > 0">
      <VTooltip
        placement="bottom"
        theme="menu"
        v-for="item in currentSelectedItems.items"
        :key="item.id"
      >
        <div class="item">
          <div class="c-outlineTag c-outlineTag--delete">
            <div class="name c-text-regular--s">{{ item.name }}</div>
            <DgrIcon name="times" size="xs" @click="removeItem(item)" />
          </div>
        </div>
        <template #popper>
          <div>{{ item.name }}<br />{{ item.hostname }}</div>
        </template>
      </VTooltip>
    </div>
  </div>
</template>

<style scoped lang="scss">
.item-select {
  box-sizing: border-box;
  position: relative;

  input {
    width: 100% !important;
    max-width: 100% !important;
  }

  .media-label {
    margin-right: 4px;
  }

  .item-list {
    box-sizing: border-box;
    position: absolute;
    z-index: var(--z-dropdown);
    max-height: 220px;
    width: 100%;
    overflow-y: scroll;
    background-color: #ffffff;
    border-radius: 4px;
    border-left: 1px solid $color-gray400;
    border-right: 1px solid $color-gray400;
    border-bottom: 1px solid $color-gray400;

    .item {
      cursor: pointer;
      display: flex;
      padding: 4px 12px;
      align-items: center;

      &.disabled {
        cursor: not-allowed;
        color: $color-gray600;
        justify-content: space-between;
      }
      &:not(.disabled):hover {
        background: $color-gray200;
      }
      &.heading {
        padding: 10px 12px 12px 12px;
        border-bottom: 1px solid $color-gray400;
      }
      .hostname,
      .count {
        color: $color-gray800;
      }
      .info {
        flex-grow: 1;
        max-width: calc(100% - 65px);
        .name,
        .hostname {
          word-wrap: nowrap;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
      .count {
        width: 65px;
        margin-left: 8px;
        text-align: right;
      }
    }
  }

  .selected-items {
    display: flex;
    flex-wrap: wrap;
    position: relative;
    margin-top: 4px;
    .item {
      display: inline-block;
      position: relative;
      cursor: default;
      margin-top: 8px;
      margin-right: 8px;
      .name {
        max-width: 94px;
        word-wrap: nowrap;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .icon-box {
        cursor: pointer;
        padding: 4px 2px;
      }
    }
  }
}
</style>
